import { useState } from 'react'
import { noteFileBytes, type NoteAttachment } from '../transport/api'
import { formatBytes } from '../ui/bytes'
import { useBlobUrl } from '../ui/useBlobUrl'
import { downloadBlob } from '../ui/blobUrl'

/**
 * Attachments on a note, displayed.
 *
 * These used to be `<img src={noteFileBlobUrl(id)}>` — a direct HTTP GET, which under
 * the portless backbone reaches nothing: the same missing lane that broke every upload
 * (owner, 2026-08-07) broke every attachment image, it just failed quieter, as broken
 * thumbnails instead of an error. So the bytes come through the relay in slices and
 * become an object URL — FileViewer's pattern.
 *
 * Images fetch eagerly, as they always rendered eagerly. Everything else fetches ON
 * CLICK: a note can carry a 100MB log, and pulling it through the relay to draw a row
 * that just says its name would make opening the panel cost the download.
 */
const isImage = (mime?: string) => !!mime && mime.startsWith('image/')

const size = formatBytes

function ImageAtt({ a }: { a: NoteAttachment }) {
  // Revoked on unmount by the hook, not leaked: every open of the panel would otherwise
  // pin another copy of every image in memory for the life of the page.
  const { url, failed: err } = useBlobUrl(() => noteFileBytes(a.file_id, a.mime), [a.file_id, a.mime])
  if (err) return <span className="nt-att">{a.name} (unavailable)</span>
  if (!url) return <span className="nt-att img nt-att-loading">{a.name}…</span>
  return (
    <a className="nt-att img" href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={a.name} loading="lazy" />
    </a>
  )
}

function FileAtt({ a }: { a: NoteAttachment }) {
  const [busy, setBusy] = useState(false)
  return (
    <button
      type="button"
      className="nt-att file"
      disabled={busy}
      onClick={() => {
        setBusy(true)
        noteFileBytes(a.file_id, a.mime)
          .then((blob) => {
            downloadBlob(blob, a.name || a.file_id)
          })
          .catch(() => {
            /* the row simply re-enables; the next click retries */
          })
          .finally(() => setBusy(false))
      }}
    >
      <span className="nt-att-name">{busy ? `${a.name} …` : a.name}</span>
      <span className="nt-att-size">{size(a.size)}</span>
    </button>
  )
}

export function NoteAttachments({ items }: { items: NoteAttachment[] }) {
  if (!items.length) return null
  return (
    <div className="nt-atts">
      {items.map((a) => (isImage(a.mime) ? <ImageAtt key={a.file_id} a={a} /> : <FileAtt key={a.file_id} a={a} />))}
    </div>
  )
}
